import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { MotionConfig } from "framer-motion";
import { ThemeProvider } from "styled-components";
import { lightTheme, darkTheme } from "../styles/theme";

const ThemeToggleContext = createContext();

const STORAGE_KEY = "pharuerp:is-dark";            // legado (mantido p/ retrocompat)
const MODE_STORAGE_KEY = "pharuerp:theme-mode";    // light | dark | system
const ACCENT_STORAGE_KEY = "pharuerp:accent-color";
const DENSITY_STORAGE_KEY = "pharuerp:density";     // compact | cozy | comfort
const ANIM_STORAGE_KEY = "pharuerp:animacoes";      // "true" | "false"

// REF-14: cores de destaque. Tokens em src/styles/canonical/tokens.css.
// Os 4 primeiros são a paleta base; os 8 "chartN" são as séries do gráfico
// (--chart-1..8) oferecidas como accent a pedido — mesma cor em claro/escuro.
export const ACCENTS = [
  { id: "violet", label: "Violeta", swatch: "#5B5BF6" },
  { id: "ocean", label: "Oceano", swatch: "#0EA5E9" },
  { id: "emerald", label: "Esmeralda", swatch: "#10B981" },
  { id: "sunset", label: "Pôr do Sol", swatch: "#F97316" },
  { id: "chart1", label: "Índigo", swatch: "#7C7CFF" },
  { id: "chart2", label: "Verde", swatch: "#34D399" },
  { id: "chart3", label: "Azul-céu", swatch: "#38BDF8" },
  { id: "chart4", label: "Âmbar", swatch: "#FBBF24" },
  { id: "chart5", label: "Coral", swatch: "#FB7185" },
  { id: "chart6", label: "Lavanda", swatch: "#C084FC" },
  { id: "chart7", label: "Turquesa", swatch: "#2DD4BF" },
  { id: "chart8", label: "Rosa-pink", swatch: "#F472B6" },
];
const ACCENT_IDS = ACCENTS.map((a) => a.id);
// Decisao de produto (Igor, 2026-08-04): o padrao deixou de ser o violeta e
// passou a ser o Por do Sol. Vale para quem nunca escolheu cor; quem escolheu
// violeta explicitamente continua com violeta (o valor salvo prevalece).
// O espelho CSS deste default vive em styles/canonical/tokens.css (:root) e no
// script de primeiro paint em public/index.html — mudar aqui exige mudar la.
const ACCENT_DEFAULT = "sunset";

const DENSITIES = ["compact", "cozy", "comfort"];
const DENSITY_DEFAULT = "cozy";

function prefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

// Fase C — modo de tema: light | dark | system. Migra a chave legada is-dark.
function readStoredMode() {
  if (typeof window === "undefined") return "light";
  try {
    const m = window.localStorage.getItem(MODE_STORAGE_KEY);
    if (m === "light" || m === "dark" || m === "system") return m;
    // migra do legado: is-dark === "true" -> dark; "false"/ausente -> light
    return window.localStorage.getItem(STORAGE_KEY) === "true" ? "dark" : "light";
  } catch {
    return "light";
  }
}

function readStoredAccent() {
  if (typeof window === "undefined") return ACCENT_DEFAULT;
  try {
    const stored = window.localStorage.getItem(ACCENT_STORAGE_KEY);
    return ACCENT_IDS.includes(stored) ? stored : ACCENT_DEFAULT;
  } catch {
    return ACCENT_DEFAULT;
  }
}

function readStoredDensity() {
  if (typeof window === "undefined") return DENSITY_DEFAULT;
  try {
    const d = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return DENSITIES.includes(d) ? d : DENSITY_DEFAULT;
  } catch {
    return DENSITY_DEFAULT;
  }
}

function readStoredAnimacoes() {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(ANIM_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
}

export function ThemeContextProvider({ children }) {
  // O estado inicial continua vindo do localStorage, e isso e proposital: e o
  // que evita a piscada. A preferencia do servidor so chega depois do /api/me,
  // ja com a tela desenhada — se dependessemos dela para o primeiro render, o
  // usuario veria o tema padrao por um instante em toda carga. O local funciona
  // como espelho: pinta na hora, e o servidor reconcilia logo em seguida.
  const [themeMode, setThemeModeState] = useState(readStoredMode);
  const [systemDark, setSystemDark] = useState(prefersDark);
  const [accentColor, setAccentColorState] = useState(readStoredAccent);
  const [densidade, setDensidadeState] = useState(readStoredDensity);
  const [animacoes, setAnimacoesState] = useState(readStoredAnimacoes);
  // Vira true quando a aparencia do servidor foi aplicada (ou quando se
  // constatou que o servidor nao tem nada). O sincronizador so comeca a GRAVAR
  // depois disso — senao o estado local recem-carregado sobrescreveria no
  // servidor a escolha que o usuario fez em outro dispositivo.
  const [hidratadoDoServidor, setHidratadoDoServidor] = useState(false);

  // isDark derivado: em "system" segue o SO; senão é explícito.
  const isDark = themeMode === "system" ? systemDark : themeMode === "dark";

  // setThemeMode aceita "light" | "dark" | "system" (retrocompat: Header chama
  // setThemeMode("light"|"dark")). `toggle` alterna entre claro/escuro explícito.
  const setThemeMode = (mode) =>
    setThemeModeState(["light", "dark", "system"].includes(mode) ? mode : "light");
  const toggle = () => setThemeModeState(isDark ? "light" : "dark");
  const setAccentColor = (color) =>
    setAccentColorState(ACCENT_IDS.includes(color) ? color : ACCENT_DEFAULT);
  const setDensidade = (d) => setDensidadeState(DENSITIES.includes(d) ? d : DENSITY_DEFAULT);
  const setAnimacoes = (v) => setAnimacoesState(Boolean(v));

  // Aplica a aparencia que veio do perfil do usuario (/api/me). Cada campo e
  // opcional: o que o servidor nao souber, mantem o que ja esta valendo.
  // `accent` NAO e checado contra ACCENT_IDS aqui de proposito — se o servidor
  // guardou uma cor que esta versao do frontend ainda nao conhece (build
  // antigo em cache, por exemplo), cair no violeta seria pior do que passar
  // adiante um valor que o CSS simplesmente ignora.
  const aplicarAparenciaDoServidor = useCallback((aparencia) => {
    if (aparencia && typeof aparencia === "object") {
      if (["light", "dark", "system"].includes(aparencia.tema)) {
        setThemeModeState(aparencia.tema);
      }
      if (typeof aparencia.accent === "string" && aparencia.accent) {
        setAccentColorState(aparencia.accent);
      }
      if (DENSITIES.includes(aparencia.densidade)) {
        setDensidadeState(aparencia.densidade);
      }
      if (typeof aparencia.animacoes === "boolean") {
        setAnimacoesState(aparencia.animacoes);
      }
    }
    setHidratadoDoServidor(true);
  }, []);

  // Chamado quando o usuario sai: a proxima conta nao pode herdar a aparencia
  // desta, e o ciclo de hidratacao precisa recomecar.
  const resetarHidratacao = useCallback(() => setHidratadoDoServidor(false), []);

  const aparenciaAtual = useMemo(
    () => ({ tema: themeMode, accent: accentColor, densidade, animacoes }),
    [themeMode, accentColor, densidade, animacoes],
  );

  // Acompanha a preferência do SO (atualiza em tempo real quando o usuário
  // troca o tema do sistema). Suporta API moderna + legada (Safari < 14).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemDark(e.matches);
    setSystemDark(mql.matches); // sincroniza no mount
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else if (mql.addListener) mql.addListener(handler);
    // Blindagem para PWA/aba em segundo plano (2026-08-04): o evento "change"
    // se perde quando a pagina esta congelada — o PWA do Windows acorda da
    // memoria SEM recarregar e SEM receber o evento que aconteceu enquanto
    // dormia, e o modo "Sistema" fica preso no tema antigo. (No iOS o WebKit
    // mata a pagina em background e a reabertura recarrega do zero, por isso
    // la "funcionava".) Reavaliar o sinal ao voltar a ficar visivel cobre o
    // buraco sem depender do evento.
    const resync = () => setSystemDark(
      window.matchMedia("(prefers-color-scheme: dark)").matches,
    );
    const aoVoltarVisivel = () => {
      if (!document.hidden) resync();
    };
    document.addEventListener("visibilitychange", aoVoltarVisivel);
    window.addEventListener("focus", resync);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else if (mql.removeListener) mql.removeListener(handler);
      document.removeEventListener("visibilitychange", aoVoltarVisivel);
      window.removeEventListener("focus", resync);
    };
  }, []);

  // Persiste modo + sincroniza [data-theme]. Mantém a chave legada is-dark
  // alinhada (true/false) para qualquer leitor antigo.
  useEffect(() => {
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, themeMode);
      window.localStorage.setItem(STORAGE_KEY, String(isDark));
    } catch {
      // ignora falha de escrita
    }
    if (typeof document !== "undefined") {
      document.documentElement.dataset.theme = isDark ? "dark" : "light";
    }
  }, [themeMode, isDark]);

  useEffect(() => {
    try {
      window.localStorage.setItem(ACCENT_STORAGE_KEY, accentColor);
    } catch { /* ignora */ }
    if (typeof document !== "undefined") {
      document.documentElement.dataset.accent = accentColor;
    }
  }, [accentColor]);

  // Fase C — densidade de tabelas: [data-density] no <html> (tokens adotados
  // progressivamente pelas tabelas via var(--ui-density-scale)).
  useEffect(() => {
    try {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, densidade);
    } catch { /* ignora */ }
    if (typeof document !== "undefined") {
      document.documentElement.dataset.density = densidade;
    }
  }, [densidade]);

  // Fase C — animações: [data-animacoes="off"] desliga transições/animações
  // globais (regra em configuracoes.css). Respeita quem prefere menos movimento.
  useEffect(() => {
    try {
      window.localStorage.setItem(ANIM_STORAGE_KEY, String(animacoes));
    } catch { /* ignora */ }
    if (typeof document !== "undefined") {
      document.documentElement.dataset.animacoes = animacoes ? "on" : "off";
    }
  }, [animacoes]);

  const value = useMemo(
    () => ({
      // retrocompat
      isDark,
      toggle,
      setThemeMode,
      accentColor,
      setAccentColor,
      accents: ACCENTS,
      // Fase C
      themeMode,
      densidade,
      setDensidade,
      animacoes,
      setAnimacoes,
      // PREF-UI: usados pelo SincronizadorPreferencias, que fica dentro do
      // AuthProvider (este provider envolve o Auth, entao nao pode ler o
      // usuario sozinho — quem faz a ponte e o componente).
      aparenciaAtual,
      aplicarAparenciaDoServidor,
      resetarHidratacao,
      hidratadoDoServidor,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isDark,
      themeMode,
      accentColor,
      densidade,
      animacoes,
      aparenciaAtual,
      aplicarAparenciaDoServidor,
      resetarHidratacao,
      hidratadoDoServidor,
    ]
  );

  return (
    <ThemeToggleContext.Provider value={value}>
      {/* Quando animações estão OFF: reducedMotion="always" pula transform/layout
          do framer-motion e transition duration 0 zera o resto. Combinado com a
          regra CSS [data-animacoes="off"] (configuracoes.css), nada anima. */}
      <MotionConfig
        reducedMotion={animacoes ? "user" : "always"}
        transition={animacoes ? undefined : { duration: 0 }}
      >
        <ThemeProvider theme={isDark ? darkTheme : lightTheme}>{children}</ThemeProvider>
      </MotionConfig>
    </ThemeToggleContext.Provider>
  );
}

export const useThemeToggle = () => useContext(ThemeToggleContext);
